import { useCallback, useRef, useState } from 'react';

import { streamChat, type ChatContextHit, type ChatHistoryItem } from '../api/chat-stream.js';
import { useApi } from '../api/use-api.js';
import { useUiStore } from '../state/ui-store.js';

export type ChatRole = 'user' | 'assistant';

export interface ChatMessage {
  readonly id: string;
  readonly role: ChatRole;
  readonly content: string;
  readonly streaming?: boolean;
  readonly contextHits?: readonly ChatContextHit[];
  readonly error?: string;
}

export interface UseChatReturn {
  readonly messages: readonly ChatMessage[];
  readonly isStreaming: boolean;
  readonly send: (text: string) => void;
  readonly clear: () => void;
  readonly stop: () => void;
}

const CITATION_RE =
  /\[\[(?:graph:)?(Class|Function|Method|Interface|File|Folder|Variable|Enum|Type|CodeElement):([^\]]+)\]\]/g;

/**
 * Manages an in-memory chat thread for the active repo. Wires retrieval hits
 * to `setHighlights('tool', ...)` as soon as the SSE `context` event lands,
 * and parses `[[Type:Name]]` citations on stream completion to flip
 * `setHighlights('citation', ...)`. Aborts in-flight streams on `clear()` /
 * `stop()` and on unmount of the consumer.
 */
export function useChat(slug: string): UseChatReturn {
  const api = useApi();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const cancelRef = useRef<(() => void) | null>(null);

  const stop = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    setIsStreaming(false);
  }, []);

  const clear = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    setIsStreaming(false);
    setMessages([]);
    useUiStore.getState().clearHighlights();
  }, []);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !slug) return;
      cancelRef.current?.();

      const history: ChatHistoryItem[] = messages
        .filter((m) => !m.error)
        .map((m) => ({ role: m.role, content: m.content }));

      const userMsg: ChatMessage = { id: makeId(), role: 'user', content: trimmed };
      const assistantId = makeId();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        streaming: true,
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      cancelRef.current = streamChat(api, slug, trimmed, history, {
        onContext: (hits) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, contextHits: hits } : m)),
          );
          useUiStore.getState().setHighlights('tool', hits.map((h) => h.id).filter(Boolean));
        },
        onToken: (delta) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + delta } : m)),
          );
        },
        onDone: () => {
          setMessages((prev) => {
            const next = prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m));
            const finished = next.find((m) => m.id === assistantId);
            if (finished) {
              const ids = parseCitationIds(finished.content);
              if (ids.length > 0) useUiStore.getState().setHighlights('citation', ids);
            }
            return next;
          });
          setIsStreaming(false);
          cancelRef.current = null;
        },
        onError: (msg) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, streaming: false, error: msg } : m)),
          );
          setIsStreaming(false);
          cancelRef.current = null;
        },
      });
    },
    [api, messages, slug],
  );

  return { messages, isStreaming, send, clear, stop };
}

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function parseCitationIds(content: string): string[] {
  const ids: string[] = [];
  for (const match of content.matchAll(CITATION_RE)) {
    const type = match[1];
    const name = match[2]?.trim();
    if (type && name) ids.push(`${type}:${name}`);
  }
  return ids;
}
